import {
  collection, addDoc, getDocs, query, orderBy, limit, startAfter, serverTimestamp, Timestamp,
  QueryDocumentSnapshot, DocumentData,
} from 'firebase/firestore';
import { auth, db } from './firebase';
import { AuditLog, AuditEntityType, AuditAction } from '../types';

export interface LogAuditEventParams {
  entityType: AuditEntityType;
  entityId: string;
  action: AuditAction;
  details?: string;
  previousValues?: Record<string, unknown>;
  newValues?: Record<string, unknown>;
}

export interface AuditLogsPage {
  logs: AuditLog[];
  cursor: QueryDocumentSnapshot<DocumentData> | null;
  hasMore: boolean;
}

export async function logAuditEvent(params: LogAuditEventParams): Promise<void> {
  const user = auth.currentUser;
  if (!user) return;
  try {
    await addDoc(collection(db, 'auditLogs'), {
      entityType: params.entityType,
      entityId: params.entityId,
      action: params.action,
      userId: user.uid,
      userEmail: user.email ?? null,
      timestamp: serverTimestamp(),
      details: params.details ?? null,
      previousValues: params.previousValues ?? null,
      newValues: params.newValues ?? null,
    });
  } catch (err) {
    console.warn('Failed to write audit log:', err);
  }
}

export async function getAuditLogs(limitCount: number = 200): Promise<AuditLog[]> {
  const q = query(
    collection(db, 'auditLogs'),
    orderBy('timestamp', 'desc'),
    limit(limitCount),
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => {
    const data = d.data();
    return {
      id: d.id,
      entityType: data.entityType as AuditLog['entityType'],
      entityId: data.entityId as string,
      action: data.action as AuditLog['action'],
      userId: data.userId as string,
      userEmail: data.userEmail ?? undefined,
      timestamp: (data.timestamp as Timestamp)?.toDate?.() ?? new Date(),
      details: data.details ?? undefined,
      previousValues: data.previousValues ?? undefined,
      newValues: data.newValues ?? undefined,
    } as AuditLog;
  });
}

export async function getAuditLogsPage(
  pageSize: number = 50,
  cursor?: QueryDocumentSnapshot<DocumentData> | null,
): Promise<AuditLogsPage> {
  const constraints = [
    orderBy('timestamp', 'desc'),
    ...(cursor ? [startAfter(cursor)] : []),
    limit(pageSize),
  ];
  const q = query(collection(db, 'auditLogs'), ...constraints);
  const snap = await getDocs(q);
  const logs = snap.docs.map((d: QueryDocumentSnapshot<DocumentData>) => {
    const data = d.data();
    return {
      id: d.id,
      entityType: data.entityType as AuditLog['entityType'],
      entityId: data.entityId as string,
      action: data.action as AuditLog['action'],
      userId: data.userId as string,
      userEmail: data.userEmail ?? undefined,
      timestamp: (data.timestamp as Timestamp)?.toDate?.() ?? new Date(),
      details: data.details ?? undefined,
      previousValues: data.previousValues ?? undefined,
      newValues: data.newValues ?? undefined,
    } as AuditLog;
  });

  return {
    logs,
    cursor: snap.docs[snap.docs.length - 1] ?? null,
    hasMore: snap.docs.length === pageSize,
  };
}

export async function addLoginEvent(): Promise<void> {
  const user = auth.currentUser;
  if (!user) return;

  return logAuditEvent({ entityType: 'user', entityId: user.uid, action: 'login' }).catch(() => {});
}
